import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Bug, PaperPlaneRight, CheckCircle, X, Info, Warning } from "@phosphor-icons/react";

const SEV = {
  low:      { label: "Faible",  cls: "bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300" },
  medium:   { label: "Moyenne", cls: "bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300" },
  high:     { label: "Haute",   cls: "bg-orange-100 text-orange-700 dark:bg-orange-950/30 dark:text-orange-300" },
  critical: { label: "Critique",cls: "bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-300" },
};
const STATUS = {
  open:        { label: "Ouvert",     cls: "bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300" },
  in_progress: { label: "En cours",   cls: "bg-amber-100 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300" },
  resolved:    { label: "Résolu",     cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300" },
  wontfix:     { label: "Non retenu", cls: "bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
};

export default function BugReports({ embedded = false }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({ title: "", description: "", severity: "medium", steps_to_reproduce: "" });
  const [busy, setBusy] = useState(false);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/bug-reports");
      setReports(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (statusFilter === "all" ? reports : reports.filter((r) => r.status === statusFilter)),
    [reports, statusFilter],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Titre et description obligatoires");
      return;
    }
    setBusy(true);
    try {
      await api.post("/bug-reports", {
        ...form,
        page_url: window.location.href,
        browser: navigator.userAgent,
      });
      toast.success("Bug signalé — les administrateurs ont été notifiés par email");
      setForm({ title: "", description: "", severity: "medium", steps_to_reproduce: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'envoi"); }
    finally { setBusy(false); }
  };

  const updateStatus = async (bug, patch) => {
    try {
      await api.patch(`/bug-reports/${bug.id}`, patch);
      toast.success("Rapport mis à jour");
      load();
      setEditing(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (bug) => {
    if (!window.confirm(`Supprimer définitivement le rapport « ${bug.title} » ?`)) return;
    try {
      await api.delete(`/bug-reports/${bug.id}`);
      toast.success("Rapport supprimé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className={embedded ? "space-y-6 p-6" : "space-y-6 max-w-5xl"}>
      {!embedded && (
        <header>
          <div className="label-caps flex items-center gap-1.5"><Bug size={12}/> Support interne</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Signaler un bug</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isAdmin
              ? "Consultez et gérez les rapports de bug remontés par l'équipe."
              : "Remontez rapidement un problème à l'administrateur. Un email lui est envoyé automatiquement."}
          </p>
        </header>
      )}
      {embedded && (
        <p className="text-sm text-muted-foreground">
          Consultez et gérez les rapports de bug remontés par l'équipe. Vous pouvez aussi en soumettre un nouveau ci-dessous.
        </p>
      )}

      {/* Submission form */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={submit} className="space-y-4" data-testid="bug-form">
            <div>
              <Label>Titre *</Label>
              <Input required maxLength={200} data-testid="bug-title"
                placeholder="Ex. Bouton Exporter PDF ne réagit pas"
                value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}/>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label>Description *</Label>
                <Textarea required rows={4} data-testid="bug-description"
                  placeholder="Ce que vous faisiez, ce qui s'est passé, ce que vous attendiez."
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}/>
              </div>
              <div>
                <Label>Sévérité</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="bug-severity"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Faible — cosmétique</SelectItem>
                    <SelectItem value="medium">Moyenne — gêne</SelectItem>
                    <SelectItem value="high">Haute — bloque une action</SelectItem>
                    <SelectItem value="critical">Critique — service HS</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-muted-foreground mt-4">
                  <b>Page envoyée :</b><br/><span className="font-mono break-all">{window.location.href}</span>
                </div>
              </div>
            </div>
            <div>
              <Label>Étapes pour reproduire <span className="text-muted-foreground text-xs">(facultatif)</span></Label>
              <Textarea rows={3} data-testid="bug-steps"
                placeholder={"1. Aller sur…\n2. Cliquer sur…\n3. Voir l'erreur"}
                value={form.steps_to_reproduce} onChange={(e) => setForm({ ...form, steps_to_reproduce: e.target.value })}/>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={busy} data-testid="bug-submit">
                <PaperPlaneRight size={16} className="mr-2"/>{busy ? "Envoi…" : "Envoyer le rapport"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Reports list */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-display font-semibold text-lg flex items-center gap-2">
              <Info size={16}/> {isAdmin ? "Tous les rapports" : "Mes rapports"}
              <span className="text-muted-foreground text-sm">({filtered.length}{statusFilter !== "all" && ` / ${reports.length}`})</span>
            </h2>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="bug-status-filter"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="open">Ouvert</SelectItem>
                <SelectItem value="in_progress">En cours</SelectItem>
                <SelectItem value="resolved">Résolu</SelectItem>
                <SelectItem value="wontfix">Non retenu</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading && <p className="text-muted-foreground text-sm">Chargement…</p>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-10 text-muted-foreground text-sm">
              {reports.length === 0 ? "Aucun bug signalé pour le moment." : "Aucun rapport pour ce filtre."}
            </div>
          )}
          {filtered.map((b) => (
            <div key={b.id} className="border-b last:border-b-0 py-4" data-testid={`bug-row-${b.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${SEV[b.severity]?.cls}`}>{SEV[b.severity]?.label}</span>
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${STATUS[b.status]?.cls}`}>{STATUS[b.status]?.label}</span>
                    <span className="text-xs text-muted-foreground">
                      #{b.id.slice(0, 8)} · {b.reporter_name} · {new Date(b.created_at).toLocaleString("fr-FR")}
                    </span>
                  </div>
                  <div className="font-medium mt-1.5">{b.title}</div>
                  <div className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{b.description}</div>
                  {b.steps_to_reproduce && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Étapes de reproduction</summary>
                      <pre className="mt-1 p-2 bg-muted rounded whitespace-pre-wrap font-mono">{b.steps_to_reproduce}</pre>
                    </details>
                  )}
                  {b.page_url && <div className="text-[11px] text-muted-foreground mt-1 font-mono break-all">📍 {b.page_url}</div>}
                  {b.admin_notes && (
                    <div className="mt-2 text-xs rounded bg-primary/5 border border-primary/20 p-2">
                      <b>Note admin :</b> <span className="whitespace-pre-wrap">{b.admin_notes}</span>
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex gap-1 flex-wrap">
                    <Button size="sm" variant="secondary" onClick={() => setEditing(b)} data-testid={`bug-edit-${b.id}`}>
                      Modifier
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(b)}
                      className="text-destructive hover:text-destructive" data-testid={`bug-delete-${b.id}`}>
                      <X size={14}/>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Admin edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Traiter le rapport</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="text-sm"><b>{editing.title}</b></div>
              <div>
                <Label>Statut</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="bug-edit-status"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Ouvert</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="resolved">Résolu</SelectItem>
                    <SelectItem value="wontfix">Non retenu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sévérité</Label>
                <Select value={editing.severity} onValueChange={(v) => setEditing({ ...editing, severity: v })}>
                  <SelectTrigger data-testid="bug-edit-severity"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Faible</SelectItem>
                    <SelectItem value="medium">Moyenne</SelectItem>
                    <SelectItem value="high">Haute</SelectItem>
                    <SelectItem value="critical">Critique</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note admin</Label>
                <Textarea rows={4} data-testid="bug-edit-notes"
                  placeholder="Ce que vous répondez au rapporteur (résolution, contournement, planning…)"
                  value={editing.admin_notes || ""}
                  onChange={(e) => setEditing({ ...editing, admin_notes: e.target.value })}/>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={() => updateStatus(editing, {
              status: editing.status, severity: editing.severity, admin_notes: editing.admin_notes,
            })} data-testid="bug-edit-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
